import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import type { RowContextMenu } from "@/components/hooks/useContextMenu";

interface MetaAccordion {
  /// `label` of the one expanded section, or null when all are collapsed.
  openLabel: string | null;
  toggle: (label: string) => void;
  open: (label: string) => void;
}

/// Absent by default: a `DatasetMetaSection` outside a `MetaSectionAccordion`
/// keeps its own collapsed state, so sections used on their own (dialogs, other
/// pages) behave exactly as before.
const MetaAccordionContext = createContext<MetaAccordion | null>(null);

/// Makes the `DatasetMetaSection`s below it mutually exclusive — expanding one
/// collapses the rest. Sections are identified by their `label`, unique within a
/// dataset row.
export const MetaSectionAccordion: React.FC<{
  defaultOpenLabel?: string | null;
  children: React.ReactNode;
}> = ({ defaultOpenLabel = null, children }) => {
  const [openLabel, setOpenLabel] = useState<string | null>(defaultOpenLabel);
  const toggle = useCallback(
    (label: string) => setOpenLabel((cur) => (cur === label ? null : label)),
    [],
  );
  const value = useMemo(
    () => ({ openLabel, toggle, open: setOpenLabel }),
    [openLabel, toggle],
  );
  return (
    <MetaAccordionContext.Provider value={value}>
      {children}
    </MetaAccordionContext.Provider>
  );
};

interface DatasetMetaSectionProps {
  icon: React.ReactNode;
  label: string;
  meta?: React.ReactNode;
  action?: React.ReactNode;
  children?: React.ReactNode;
  headingPl?: string;
  bodyPl?: string;
  pr?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  /// Count of body items; an increase auto-expands a collapsed section so a
  /// freshly added label/link/value is never hidden behind the collapsed state.
  itemCount?: number;
  /// Color/weight utility for the heading label. Defaults to the muted section
  /// header; override (e.g. `text-accent`) to emphasize it in a dialog.
  labelClassName?: string;
}

export const DatasetMetaSection: React.FC<DatasetMetaSectionProps> = ({
  icon,
  label,
  meta,
  action,
  children,
  headingPl = "pl-8",
  bodyPl = "pl-16",
  pr = "pr-2",
  collapsible = false,
  defaultCollapsed = false,
  itemCount,
  labelClassName = "text-fg-muted-semibold",
}) => {
  const hasBody = React.Children.toArray(children).length > 0;
  const canCollapse = collapsible && hasBody;
  const accordion = useContext(MetaAccordionContext);
  const [localCollapsed, setLocalCollapsed] = useState(defaultCollapsed);
  const isCollapsed =
    canCollapse && (accordion ? accordion.openLabel !== label : localCollapsed);

  const toggleCollapsed = () => {
    if (accordion) accordion.toggle(label);
    else setLocalCollapsed((c) => !c);
  };

  const accordionOpen = accordion?.open;
  const expand = useCallback(() => {
    if (accordionOpen) accordionOpen(label);
    else setLocalCollapsed(false);
  }, [accordionOpen, label]);

  const prevItemCount = useRef(itemCount);
  useEffect(() => {
    if (
      itemCount !== undefined &&
      prevItemCount.current !== undefined &&
      itemCount > prevItemCount.current
    ) {
      expand();
    }
    prevItemCount.current = itemCount;
  }, [itemCount, expand]);

  const iconSlot = canCollapse ? (
    <span className="relative flex items-center justify-center size-6 shrink-0 text-fg-muted">
      <span className="flex transition-opacity group-hover/heading:opacity-0">
        {icon}
      </span>
      <KeyboardArrowDownOutlinedIcon
        className="icon-sm absolute opacity-0 transition-[opacity,transform] group-hover/heading:opacity-100"
        style={{ transform: isCollapsed ? "rotate(-90deg)" : "none" }}
      />
    </span>
  ) : (
    <span className="flex items-center justify-center size-6 shrink-0 text-fg-muted">
      {icon}
    </span>
  );

  const labelEl = (
    <span
      className={`${labelClassName} select-none`}
      style={{ fontWeight: "var(--font-weight-medium)" }}
    >
      {label}
    </span>
  );

  return (
    <div className={`${pr} py-2`}>
      <div
        className={`flex items-center gap-2 min-h-(--control-height-sm) ${headingPl}`}
      >
        {canCollapse ? (
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-expanded={!isCollapsed}
            className="group/heading flex items-center gap-2 min-w-0 cursor-pointer bg-transparent border-0 p-0 text-left"
          >
            {iconSlot}
            {labelEl}
          </button>
        ) : (
          <>
            {iconSlot}
            {labelEl}
          </>
        )}
        {meta}
        {action && <div className="ml-auto">{action}</div>}
      </div>
      {hasBody && (
        <div
          className="grid"
          style={{
            gridTemplateRows: isCollapsed ? "0fr" : "1fr",
            transition:
              "grid-template-rows var(--duration-short) var(--ease-move)",
            transitionDelay: isCollapsed ? "var(--duration-micro)" : "0ms",
          }}
        >
          <div className="overflow-hidden" style={{ minHeight: 0 }}>
            <div
              className={`mt-2 ${bodyPl}`}
              style={{
                opacity: isCollapsed ? 0 : 1,
                transition: "opacity var(--duration-micro) var(--ease-move)",
                transitionDelay: isCollapsed ? "0ms" : "var(--duration-short)",
              }}
            >
              {children}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface MetaAddButtonProps {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  /// Row-menu trigger props for pills that offer a second create variant. Unset
  /// leaves the browser's own menu, so nothing is hidden from users with no
  /// alternative to pick.
  menuTriggerProps?: RowContextMenu["triggerProps"];
  /** Extra classes appended last — e.g. `rounded-r-none` in a split control. */
  className?: string;
  /** Overrides the leading `+` where the action is not an add. */
  icon?: React.ReactNode;
  /** Set when the pill opens a menu instead of acting at once. */
  ariaExpanded?: boolean;
  /** What it opens, when that is not a menu. */
  ariaHasPopup?: "menu" | "dialog";
}

/// The dashed "+ Add …" pill every metadata section uses for its add/empty
/// affordance. A real <button> so it can be disabled and targeted in tests.
export const MetaAddButton: React.FC<MetaAddButtonProps> = ({
  label,
  onClick,
  disabled,
  title,
  menuTriggerProps,
  className = "",
  icon = <AddOutlinedIcon className="icon-xs" />,
  ariaExpanded,
  ariaHasPopup = "menu",
}) => (
  <button
    type="button"
    onClick={onClick}
    {...menuTriggerProps}
    disabled={disabled}
    title={title}
    aria-haspopup={ariaExpanded === undefined ? undefined : ariaHasPopup}
    aria-expanded={ariaExpanded}
    className={`inline-flex items-center gap-1 shrink-0 min-h-[var(--control-height-sm)] px-2 py-0.5 rounded-app border border-dashed border-border text-fg-muted cursor-pointer hover:text-accent hover:border-accent hover:bg-accent-15 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-fg-muted disabled:hover:border-border disabled:hover:bg-transparent transition-colors ${className}`}
    style={{ fontSize: "var(--font-size-small)" }}
  >
    {icon}
    {label}
  </button>
);
